import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import { getUserInfo } from "../../../api/axios";
import {
  GOAL_STATUS,
  agreeGoalAsManager,
  approveGoal,
  completeGoal,
  discussGoalAsManager,
  getAppraisalGoal,
  getAppraisalObjectives,
  getAppraisalRatingScale,
  getMyGoals,
  getTeamGoals,
  goalStatusLabel,
  rateGoalAsUser,
  saveAppraisalGoal,
  submitGoalForApproval,
} from "./appraisalApi";
import {
  AppraisalGuideButton,
  AppraisalHelpModal,
  AppraisalSectionHeader,
} from "./AppraisalHelp";
import {
  buildRatingGuide,
  getAppraisalRoleId,
  getRatingLabel,
  normalizeRatingLabels,
} from "./appraisalAccess";
import { APPRAISAL_LIMITS, clampLen, validateFields } from "./appraisalValidation";
import { AppraisalCharCount } from "./AppraisalFormFields";
import { AppraisalEmployeeCell } from "./AppraisalEmployeeCell";
import "./Appraisal.css";

/** Goal / KPI ratings use company Goal rating max (Settings). Progress is always 0–100%. */
const GOAL_RATING_TYPE = 3;
const DEFAULT_GOAL_MAX = 5;
const emptyForm = () => ({
  id: "",
  appraisalObjectiveId: "",
  headline: "",
  objective: "",
  milestones: "",
  measurements: "",
  startDate: new Date().toISOString().slice(0, 10),
  completeBy: new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10),
  kpis: [],
});

const getCurrentUserId = () => {
  const userInfo = getUserInfo();
  if (!userInfo) return "";
  return String(
    userInfo.selfUserId ||
      userInfo.id ||
      userInfo.userId ||
      userInfo.Id ||
      userInfo.UserId ||
      ""
  ).trim();
};

const normalizeGoal = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  return {
    ...raw,
    id: raw.id ?? raw.Id,
    userId: raw.userId ?? raw.UserId ?? "",
    userName: raw.userName ?? raw.UserName ?? "",
    designation: raw.designation ?? raw.Designation ?? "",
    department: raw.department ?? raw.Department ?? "",
    officeName: raw.officeName ?? raw.OfficeName ?? "",
    profileImageUrl: String(raw.profileImageUrl ?? raw.ProfileImageUrl ?? "").trim() || null,
    managerId: raw.managerId ?? raw.ManagerId ?? "",
    managerName: raw.managerName ?? raw.ManagerName ?? "",
    status: Number(raw.status ?? raw.Status ?? 0),
    headline: raw.headline ?? raw.Headline ?? "",
    objective: raw.objective ?? raw.Objective ?? "",
    objectiveName: raw.objectiveName ?? raw.ObjectiveName ?? "",
    appraisalObjectiveId: raw.appraisalObjectiveId ?? raw.AppraisalObjectiveId ?? "",
    milestones: raw.milestones ?? raw.Milestones ?? "",
    measurements: raw.measurements ?? raw.Measurements ?? "",
    startDate: raw.startDate ?? raw.StartDate,
    completeBy: raw.completeBy ?? raw.CompleteBy,
    userGoalRating: raw.userGoalRating ?? raw.UserGoalRating,
    goalRating: raw.goalRating ?? raw.GoalRating,
    rateProgress: raw.rateProgress ?? raw.RateProgress,
    goalComment: raw.goalComment ?? raw.GoalComment ?? "",
    managerComment: raw.managerComment ?? raw.ManagerComment ?? "",
    discussionComments: raw.discussionComments ?? raw.DiscussionComments ?? "",
    kpis: raw.kpis ?? raw.Kpis ?? [],
  };
};

const teamGoalActionLabel = (status) => {
  switch (Number(status)) {
    case 1:
      return "Review";
    case 3:
      return "Agree";
    case 5:
      return "Discuss";
    case 6:
      return "View";
    default:
      return "Open";
  }
};

/** mode: "mine" | "team" | "detail" */
const AppraisalGoalsPage = ({ mode = "mine" }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [rows, setRows] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [goal, setGoal] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [showForm, setShowForm] = useState(false);
  const [actionForm, setActionForm] = useState({
    userGoalRating: "",
    goalRating: "",
    rateProgress: "",
    goalComment: "",
    managerComment: "",
    discussionComments: "",
  });
  const [goalMax, setGoalMax] = useState(DEFAULT_GOAL_MAX);
  const [goalLabels, setGoalLabels] = useState([]);
  const [helpKey, setHelpKey] = useState(null);
  const [savingGoal, setSavingGoal] = useState(false);
  const [teamDetailId, setTeamDetailId] = useState(null);

  const isTeam = mode === "team";
  const detailId = isTeam ? teamDetailId : id || null;
  const showingDetail = Boolean(detailId) || mode === "detail";
  const fromTeam =
    isTeam ||
    location.state?.from === "team" ||
    new URLSearchParams(location.search || "").get("from") === "team";

  const title = useMemo(() => {
    if (isTeam) return t("app.appraisal.teamGoalsTitle", { defaultValue: "Team Goals" });
    if (showingDetail) return t("app.appraisal.goalDetailTitle", { defaultValue: "Goal Detail" });
    return t("app.appraisal.myGoalsTitle", { defaultValue: "My Goals" });
  }, [showingDetail, isTeam, t]);

  const actor = useMemo(() => {
    const userId = getCurrentUserId();
    const roleId = getAppraisalRoleId();
    const isOwner = Boolean(goal?.userId && String(goal.userId) === userId);
    const isAssignedManager = Boolean(goal?.managerId && String(goal.managerId) === userId);
    const isAdmin = roleId === 1 || roleId === 2;
    // Admin may manage others' goals, never self-approve their own employee goal as "manager".
    const canManage = isAssignedManager || (isAdmin && !isOwner);
    return { userId, roleId, isOwner, isAssignedManager, isAdmin, canManage };
  }, [goal]);

  const status = Number(goal?.status ?? -1);

  const canEdit =
    Boolean(goal) &&
    actor.isOwner &&
    (status === GOAL_STATUS.Draft || status === GOAL_STATUS.ManagerDiscussed);

  const goalGuide = useMemo(
    () => buildRatingGuide(goalMax, goalLabels),
    [goalMax, goalLabels]
  );

  const formatScore = (value) => {
    if (value == null || value === "" || Number(value) <= 0) return "—";
    const n = Number(value);
    return `${n} — ${getRatingLabel(n, goalMax, goalLabels)}`;
  };

  const clampProgress = (raw) => {
    if (raw === "" || raw == null) return "";
    const n = Number(raw);
    if (Number.isNaN(n)) return "";
    return Math.min(100, Math.max(0, Math.round(n)));
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (detailId) {
        const [detail, objs, scale] = await Promise.all([
          getAppraisalGoal(detailId),
          getAppraisalObjectives(),
          getAppraisalRatingScale().catch(() => null),
        ]);
        const max =
          Number(scale?.goalRatingScale ?? scale?.GoalRatingScale) > 0
            ? Number(scale.goalRatingScale ?? scale.GoalRatingScale)
            : DEFAULT_GOAL_MAX;
        const rawCustoms = Array.isArray(scale?.customScales ?? scale?.CustomScales)
          ? scale.customScales ?? scale.CustomScales
          : [];
        const goalCustoms = normalizeRatingLabels(
          rawCustoms.filter((c) => Number(c.ratingType ?? c.RatingType) === GOAL_RATING_TYPE)
        );
        setGoalMax(max);
        setGoalLabels(goalCustoms);

        const normalized = normalizeGoal(detail);
        setGoal(normalized);
        setObjectives(objs || []);
        setForm({
          id: normalized.id,
          appraisalObjectiveId: normalized.appraisalObjectiveId || "",
          headline: normalized.headline || "",
          objective: normalized.objective || "",
          milestones: normalized.milestones || "",
          measurements: normalized.measurements || "",
          startDate: normalized.startDate ? String(normalized.startDate).slice(0, 10) : "",
          completeBy: normalized.completeBy ? String(normalized.completeBy).slice(0, 10) : "",
          kpis: normalized.kpis || [],
        });
        setActionForm({
          userGoalRating: normalized.userGoalRating ?? "",
          goalRating: normalized.goalRating ?? "",
          rateProgress: normalized.rateProgress ?? "",
          goalComment: normalized.goalComment || "",
          managerComment: normalized.managerComment || "",
          discussionComments: normalized.discussionComments || "",
        });
      } else {
        const [list, objs] = await Promise.all([
          isTeam ? getTeamGoals() : getMyGoals(),
          getAppraisalObjectives(),
        ]);
        setRows(Array.isArray(list) ? list.map(normalizeGoal) : []);
        setObjectives(objs || []);
      }
    } catch (e) {
      setError(e?.message || "Failed to load goals");
    } finally {
      setLoading(false);
    }
  }, [detailId, isTeam]);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = async (e) => {
    e.preventDefault();
    if (savingGoal) return;
    setMessage("");
    setError("");
    const validationError = validateFields([
      { value: form.headline, required: true, max: APPRAISAL_LIMITS.headline, label: "Headline" },
      { value: form.objective, max: APPRAISAL_LIMITS.longText, label: "Description" },
      { value: form.milestones, max: APPRAISAL_LIMITS.longText, label: "Milestones" },
      { value: form.measurements, max: APPRAISAL_LIMITS.longText, label: "Measurements" },
    ]);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSavingGoal(true);
    try {
      const saved = await saveAppraisalGoal({
        ...form,
        startDate: form.startDate,
        completeBy: form.completeBy,
      });
      setMessage(
        t("app.appraisal.goals.saved", { defaultValue: "Goal saved." })
      );
      setShowForm(false);
      setForm(emptyForm());
      if (saved?.id && !showingDetail) {
        history.push({
          pathname: `/appraisal/goals/${saved.id}`,
          state: fromTeam ? { from: "team" } : undefined,
        });
      } else await load();
    } catch (err) {
      setError(err?.message || "Failed to save goal");
    } finally {
      setSavingGoal(false);
    }
  };

  const openNewGoalForm = () => {
    setForm(emptyForm());
    setShowForm(true);
    setError("");
    setMessage("");
  };

  const runAction = async (fn, payload = {}) => {
    setMessage("");
    setError("");
    try {
      await fn({ goalId: goal.id, ...payload });
      setMessage("Updated.");
      await load();
    } catch (err) {
      setError(err?.message || "Action failed");
    }
  };

  const openGoal = (goalId) => {
    if (isTeam) {
      setTeamDetailId(goalId);
      setError("");
      setMessage("");
      return;
    }
    history.push(`/appraisal/goals/${goalId}`);
  };

  const renderRatingButtons = (field, currentValue) => (
    <div
      className="appraisal-rating-row"
      role="group"
      aria-label={t("app.appraisal.ratingPick", { defaultValue: "Select a rating" })}
    >
      {goalGuide.map(({ n, label }) => (
        <button
          key={n}
          type="button"
          className={`appraisal-rating-btn ${Number(currentValue) === n ? "active" : ""}`}
          title={`${n} — ${label}`}
          aria-label={`${n}: ${label}`}
          onClick={() => setActionForm((p) => ({ ...p, [field]: n }))}
        >
          <span className="appraisal-rating-btn__num">{n}</span>
          <span className="appraisal-rating-btn__label">{label}</span>
        </button>
      ))}
    </div>
  );

  const submitEmployeeRating = () => {
    const rating = Number(actionForm.userGoalRating);
    const progress = clampProgress(actionForm.rateProgress);
    if (!rating || rating < 1 || rating > goalMax) {
      setError(`Select a rating from 1 to ${goalMax}.`);
      return;
    }
    if (progress === "" || progress < 0 || progress > 100) {
      setError("Progress must be between 0 and 100%.");
      return;
    }
    runAction(rateGoalAsUser, {
      userGoalRating: rating,
      rateProgress: Number(progress),
      goalComment: actionForm.goalComment,
    });
  };

  const submitManagerAgree = () => {
    const rating = Number(actionForm.goalRating);
    if (!rating || rating < 1 || rating > goalMax) {
      setError(`Select a manager rating from 1 to ${goalMax}.`);
      return;
    }
    runAction(agreeGoalAsManager, {
      goalRating: rating,
      managerComment: actionForm.managerComment,
    });
  };

  return (
    <div className={`appraisal-page${isTeam ? "" : " appraisal-my-goals-page"}`}>
      <CommonPageHeader
        icon={<i className="bi bi-bullseye" />}
        title={title}
        description={
          showingDetail
            ? goal?.headline || ""
            : isTeam
              ? t("app.appraisal.teamGoalsSubtitle", {
                  defaultValue:
                    "Review and approve goals for your direct reports — open a goal to assess, agree, or discuss.",
                })
              : t("app.appraisal.goalsSubtitle", {
                  defaultValue: "Create, track, and review goals with your manager.",
                })
        }
        rightContent={
          !showingDetail ? (
            <div className="appraisal-page-header-actions">
              <AppraisalGuideButton onClick={() => setHelpKey("goals")} />
              {!isTeam ? (
                <button
                  type="button"
                  className="appraisal-btn primary appraisal-header-cta"
                  onClick={openNewGoalForm}
                >
                  <i className="bi bi-plus-lg" aria-hidden />
                  {t("app.appraisal.goals.newGoal", { defaultValue: "New goal" })}
                </button>
              ) : null}
            </div>
          ) : null
        }
      />
      {loading ? <div className="appraisal-muted">Loading…</div> : null}
      {error ? <div className="appraisal-error">{error}</div> : null}
      {message ? <div className="appraisal-success">{message}</div> : null}

      {!showingDetail && !isTeam ? (
        <section className="appraisal-settings-panel appraisal-goals-panel">
          <div className="appraisal-settings-panel__head appraisal-goals-panel__head">
            <div>
              <AppraisalSectionHeader
                icon="bi-bullseye"
                title={t("app.appraisal.goals.helpCardTitle", { defaultValue: "My goals" })}
                helpKey="goals"
                onOpenHelp={setHelpKey}
                tone="goals"
              />
              <p className="appraisal-framework-hint">
                {t("app.appraisal.goals.hint", {
                  defaultValue:
                    "Draft → Submit → Rate → Complete (after manager Agree). Your manager Approves, then Agrees or Discusses.",
                })}
              </p>
            </div>
            <div className="appraisal-goals-panel__stats">
              <div className="appraisal-goals-stat">
                <strong>{rows.length}</strong>
                <span>{t("app.appraisal.goals.totalLabel", { defaultValue: "Total" })}</span>
              </div>
              <div className="appraisal-goals-stat">
                <strong>
                  {rows.filter((r) => Number(r.status) !== GOAL_STATUS.Completed).length}
                </strong>
                <span>{t("app.appraisal.goals.activeLabel", { defaultValue: "Active" })}</span>
              </div>
            </div>
          </div>

          {showForm ? (
            <form onSubmit={onSave} className="appraisal-framework-form appraisal-goals-create">
              <div className="appraisal-goals-create__title">
                <strong>
                  {t("app.appraisal.goals.createTitle", { defaultValue: "Create a new goal" })}
                </strong>
                <p>
                  {t("app.appraisal.goals.createHint", {
                    defaultValue: "Give it a clear headline and due date. You can refine details after saving.",
                  })}
                </p>
              </div>
              <label className="appraisal-field">
                {t("app.appraisal.goals.headline", { defaultValue: "Headline" })}
                <input
                  required
                  disabled={savingGoal}
                  maxLength={APPRAISAL_LIMITS.headline}
                  placeholder={t("app.appraisal.goals.headlinePlaceholder", {
                    defaultValue: "e.g. Cut auth-service flaky tests by 50% by Sept",
                  })}
                  value={form.headline}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      headline: clampLen(e.target.value, APPRAISAL_LIMITS.headline),
                    }))
                  }
                />
                <AppraisalCharCount value={form.headline} max={APPRAISAL_LIMITS.headline} />
              </label>
              <label className="appraisal-field">
                {t("app.appraisal.goals.objectiveTemplate", {
                  defaultValue: "Objective template",
                })}
                <select
                  disabled={savingGoal}
                  value={form.appraisalObjectiveId}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, appraisalObjectiveId: e.target.value }))
                  }
                >
                  <option value="">
                    {t("app.appraisal.goals.objectiveOptional", {
                      defaultValue: "— Optional —",
                    })}
                  </option>
                  {objectives.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="appraisal-field">
                {t("app.appraisal.goals.description", { defaultValue: "Description" })}
                <textarea
                  rows={3}
                  disabled={savingGoal}
                  maxLength={APPRAISAL_LIMITS.longText}
                  placeholder={t("app.appraisal.goals.descPlaceholder", {
                    defaultValue: "What success looks like for this goal",
                  })}
                  value={form.objective}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      objective: clampLen(e.target.value, APPRAISAL_LIMITS.longText),
                    }))
                  }
                />
                <AppraisalCharCount value={form.objective} max={APPRAISAL_LIMITS.longText} />
              </label>
              <div className="appraisal-grid-2">
                <label className="appraisal-field">
                  {t("app.appraisal.goals.start", { defaultValue: "Start" })}
                  <input
                    type="date"
                    required
                    disabled={savingGoal}
                    value={form.startDate}
                    onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))}
                  />
                </label>
                <label className="appraisal-field">
                  {t("app.appraisal.goals.completeBy", { defaultValue: "Complete by" })}
                  <input
                    type="date"
                    required
                    disabled={savingGoal}
                    value={form.completeBy}
                    onChange={(e) => setForm((p) => ({ ...p, completeBy: e.target.value }))}
                  />
                </label>
              </div>
              <div className="appraisal-framework-form__actions">
                <button
                  type="button"
                  className="appraisal-btn"
                  disabled={savingGoal}
                  onClick={() => {
                    setShowForm(false);
                    setForm(emptyForm());
                  }}
                >
                  {t("app.common.cancel", { defaultValue: "Cancel" })}
                </button>
                <button type="submit" className="appraisal-btn primary" disabled={savingGoal}>
                  {savingGoal
                    ? t("app.common.saving", { defaultValue: "Saving…" })
                    : t("app.appraisal.goals.saveGoal", { defaultValue: "Save goal" })}
                </button>
              </div>
            </form>
          ) : null}

          <div className="appraisal-framework-list-block">
            <div className="appraisal-framework-list-block__head">
              <span>
                {t("app.appraisal.goals.listTitle", { defaultValue: "Your goals" })}
              </span>
              <span className="appraisal-framework-count">{rows.length}</span>
            </div>

            {!loading && rows.length === 0 ? (
              <div className="appraisal-framework-empty">
                <i className="bi bi-bullseye" aria-hidden />
                <p>
                  {t("app.appraisal.noGoals", {
                    defaultValue: "No goals yet. Create your first goal to get started.",
                  })}
                </p>
                {!showForm ? (
                  <button
                    type="button"
                    className="appraisal-btn primary"
                    onClick={openNewGoalForm}
                  >
                    <i className="bi bi-plus-lg" aria-hidden />
                    {t("app.appraisal.goals.newGoal", { defaultValue: "New goal" })}
                  </button>
                ) : null}
              </div>
            ) : null}

            {!loading && rows.length > 0 ? (
              <div className="appraisal-table-wrap appraisal-team-table-wrap appraisal-goals-table-wrap">
                <table className="appraisal-table appraisal-team-table">
                  <thead>
                    <tr>
                      <th>{t("app.appraisal.colHeadline", { defaultValue: "Headline" })}</th>
                      <th>{t("app.appraisal.colDue", { defaultValue: "Due" })}</th>
                      <th>{t("app.appraisal.colStatus", { defaultValue: "Status" })}</th>
                      <th className="appraisal-team-col-actions">
                        {t("app.appraisal.colActions", { defaultValue: "Actions" })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td>
                          <div className="appraisal-goal-headline">{r.headline || "—"}</div>
                          {r.objectiveName ? (
                            <div className="appraisal-goal-objective-meta">{r.objectiveName}</div>
                          ) : null}
                        </td>
                        <td className="appraisal-team-col-date">
                          {r.completeBy
                            ? new Date(r.completeBy).toLocaleDateString()
                            : "—"}
                        </td>
                        <td>
                          <span
                            className={`appraisal-status-pill appraisal-status-pill--goal-${Number(r.status) || 0}`}
                          >
                            {goalStatusLabel(r.status)}
                          </span>
                        </td>
                        <td className="appraisal-team-col-actions">
                          <button
                            type="button"
                            className="appraisal-btn primary appraisal-team-action"
                            onClick={() => openGoal(r.id)}
                          >
                            <i className="bi bi-box-arrow-up-right" aria-hidden />
                            {t("app.appraisal.open", { defaultValue: "Open" })}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {!showingDetail && isTeam ? (
        <>
          <section className="appraisal-card">
            <AppraisalSectionHeader
              icon="bi-bullseye"
              title={t("app.appraisal.goals.helpCardTitle", { defaultValue: "Goals" })}
              helpKey="goals"
              onOpenHelp={setHelpKey}
              tone="goals"
            />
            <p className="appraisal-framework-hint" style={{ marginBottom: 0 }}>
              {t("app.appraisal.goals.hint", {
                defaultValue:
                  "Employee: Draft → Submit → Rate → Complete (after Agree). Manager: Approve → Agree/Discuss → Complete.",
              })}
            </p>
          </section>

          {!loading && rows.length === 0 ? (
            <div className="appraisal-team-empty">
              <i className="bi bi-bullseye" aria-hidden />
              <p>
                {t("app.appraisal.noTeamGoals", {
                  defaultValue: "No team goals yet.",
                })}
              </p>
            </div>
          ) : null}

          {!loading && rows.length > 0 ? (
            <div className="appraisal-table-wrap appraisal-team-table-wrap">
              <table className="appraisal-table appraisal-team-table">
                <thead>
                  <tr>
                    <th className="appraisal-team-col-employee">
                      {t("app.appraisal.colEmployee", { defaultValue: "Employee" })}
                    </th>
                    <th>{t("app.appraisal.colHeadline", { defaultValue: "Headline" })}</th>
                    <th>{t("app.appraisal.colDue", { defaultValue: "Due" })}</th>
                    <th>{t("app.appraisal.colStatus", { defaultValue: "Status" })}</th>
                    <th className="appraisal-team-col-actions">
                      {t("app.appraisal.colActions", { defaultValue: "Actions" })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                      <tr key={r.id}>
                        <td className="appraisal-team-col-employee">
                          <AppraisalEmployeeCell
                            name={r.userName}
                            designation={r.designation}
                            department={r.department}
                            officeName={r.officeName}
                            profileImageUrl={r.profileImageUrl}
                          />
                        </td>
                        <td>
                          <div className="appraisal-goal-headline">{r.headline || "—"}</div>
                          {r.objectiveName ? (
                            <div className="appraisal-goal-objective-meta">{r.objectiveName}</div>
                          ) : null}
                        </td>
                        <td className="appraisal-team-col-date">
                          {r.completeBy
                            ? new Date(r.completeBy).toLocaleDateString()
                            : "—"}
                        </td>
                        <td>
                          <span
                            className={`appraisal-status-pill appraisal-status-pill--goal-${Number(r.status) || 0}`}
                          >
                            {goalStatusLabel(r.status)}
                          </span>
                        </td>
                        <td className="appraisal-team-col-actions">
                          <button
                            type="button"
                            className="appraisal-btn primary appraisal-team-action"
                            onClick={() => openGoal(r.id)}
                          >
                            <i className="bi bi-box-arrow-up-right" aria-hidden />
                            {t(`app.appraisal.teamGoalAction.${Number(r.status)}`, {
                              defaultValue: teamGoalActionLabel(r.status),
                            })}
                          </button>
                        </td>
                      </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : null}

      {showingDetail && goal && (
        <>
          <div className="appraisal-card appraisal-checkin-detail">
            {isTeam ? (
              <button
                type="button"
                className="appraisal-checkin-back"
                onClick={() => {
                  setTeamDetailId(null);
                  setGoal(null);
                }}
              >
                <i className="bi bi-arrow-left" aria-hidden />
                {t("app.appraisal.back", { defaultValue: "Back" })}
              </button>
            ) : (
              <Link
                className="appraisal-checkin-back"
                to={fromTeam ? "/appraisal/team-goals" : "/appraisal/goals"}
              >
                <i className="bi bi-arrow-left" aria-hidden />
                {t("app.appraisal.back", { defaultValue: "Back" })}
              </Link>
            )}
            <div className="appraisal-checkin-detail__head">
              <div>
                <h3>{goal.headline}</h3>
                <p className="appraisal-checkin-detail__meta">
                  {goal.userName ? <span>{goal.userName}</span> : null}
                  {goal.designation ? <span>{goal.designation}</span> : null}
                  {goal.objectiveName ? <span>{goal.objectiveName}</span> : null}
                  {actor.isOwner ? <span>You (owner)</span> : null}
                  {actor.canManage ? <span>You (manager)</span> : null}
                </p>
              </div>
              <span
                className={`appraisal-status-pill appraisal-status-pill--goal-${Number(goal.status) || 0}`}
              >
                {goalStatusLabel(goal.status)}
              </span>
            </div>
            {goal.objective ? <p>{goal.objective}</p> : null}
            <div className="appraisal-grid-2">
              <div>
                <strong>Start:</strong> {goal.startDate ? new Date(goal.startDate).toLocaleDateString() : "—"}
              </div>
              <div>
                <strong>Due:</strong>{" "}
                {goal.completeBy ? new Date(goal.completeBy).toLocaleDateString() : "—"}
              </div>
            </div>
            {(goal.userGoalRating != null && Number(goal.userGoalRating) > 0) ||
            (goal.goalRating != null && Number(goal.goalRating) > 0) ||
            goal.rateProgress != null ? (
              <div className="appraisal-readonly-scores" style={{ marginTop: "0.75rem" }}>
                <span>Employee rating: {formatScore(goal.userGoalRating)}</span>
                <span>Progress: {goal.rateProgress != null ? `${goal.rateProgress}%` : "—"}</span>
                <span>Manager rating: {formatScore(goal.goalRating)}</span>
              </div>
            ) : null}
          </div>

          {canEdit && (
            <form className="appraisal-card" onSubmit={onSave}>
              <h4>Edit goal</h4>
              <label className="appraisal-field">
                Headline
                <input
                  required
                  maxLength={APPRAISAL_LIMITS.headline}
                  value={form.headline}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      headline: clampLen(e.target.value, APPRAISAL_LIMITS.headline),
                    }))
                  }
                />
                <AppraisalCharCount value={form.headline} max={APPRAISAL_LIMITS.headline} />
              </label>
              <label className="appraisal-field">
                Description
                <textarea
                  rows={3}
                  maxLength={APPRAISAL_LIMITS.longText}
                  value={form.objective}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      objective: clampLen(e.target.value, APPRAISAL_LIMITS.longText),
                    }))
                  }
                />
                <AppraisalCharCount value={form.objective} max={APPRAISAL_LIMITS.longText} />
              </label>
              <label className="appraisal-field">
                Milestones
                <textarea
                  rows={2}
                  maxLength={APPRAISAL_LIMITS.longText}
                  value={form.milestones}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      milestones: clampLen(e.target.value, APPRAISAL_LIMITS.longText),
                    }))
                  }
                />
                <AppraisalCharCount value={form.milestones} max={APPRAISAL_LIMITS.longText} />
              </label>
              <label className="appraisal-field">
                Measurements
                <textarea
                  rows={2}
                  maxLength={APPRAISAL_LIMITS.longText}
                  value={form.measurements}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      measurements: clampLen(e.target.value, APPRAISAL_LIMITS.longText),
                    }))
                  }
                />
                <AppraisalCharCount value={form.measurements} max={APPRAISAL_LIMITS.longText} />
              </label>
              <button type="submit" className="appraisal-btn primary">
                Save changes
              </button>
            </form>
          )}

          <section className="appraisal-card appraisal-workflow-panel">
            <div className="appraisal-workflow-panel__head">
              <span className="appraisal-workflow-panel__icon" aria-hidden>
                <i className="bi bi-diagram-3" />
              </span>
              <div>
                <h4>Workflow</h4>
                <p className="appraisal-workflow-panel__hint">
                  Status: {goalStatusLabel(goal.status)}. Take the next step below.
                </p>
              </div>
              <span
                className={`appraisal-status-pill appraisal-status-pill--goal-${Number(goal.status) || 0}`}
              >
                {goalStatusLabel(goal.status)}
              </span>
            </div>
            <div className="appraisal-workflow-panel__body">

            {actor.isOwner && (status === GOAL_STATUS.Draft || status === GOAL_STATUS.ManagerDiscussed) ? (
              <div className="appraisal-actions" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <button
                  type="button"
                  className="appraisal-btn primary"
                  onClick={() => runAction(submitGoalForApproval)}
                >
                  Submit for approval
                </button>
              </div>
            ) : null}

            {actor.isOwner && status === GOAL_STATUS.ReadyForApproval ? (
              <p className="appraisal-framework-hint">
                Waiting for your manager to approve this goal.
              </p>
            ) : null}

            {actor.canManage && status === GOAL_STATUS.ReadyForApproval ? (
              <div className="appraisal-actions" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <button
                  type="button"
                  className="appraisal-btn primary"
                  onClick={() => runAction(approveGoal)}
                >
                  Approve (manager)
                </button>
              </div>
            ) : null}

            {actor.isOwner &&
            (status === GOAL_STATUS.Approved || status === GOAL_STATUS.ManagerDiscussed) ? (
              <div style={{ marginTop: "0.75rem" }}>
                <p className="appraisal-framework-hint" style={{ marginTop: 0 }}>
                  Rate this goal on the company scale (1–{goalMax}). Progress is 0–100%.
                </p>
                <div className="appraisal-rating-legend-wrap" style={{ marginBottom: "0.75rem" }}>
                  <div className="appraisal-rating-legend-head">
                    <span className="appraisal-rating-legend-title">Your rating</span>
                  </div>
                  <ul className="appraisal-rating-legend">
                    {goalGuide.map(({ n, label }) => (
                      <li key={n}>
                        <strong>{n}</strong>
                        <span>{label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {renderRatingButtons("userGoalRating", actionForm.userGoalRating)}
                <div className="appraisal-actions" style={{ flexWrap: "wrap", gap: "0.5rem", marginTop: "0.75rem" }}>
                  <label className="appraisal-field" style={{ minWidth: 140 }}>
                    Progress %
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      value={actionForm.rateProgress}
                      onChange={(e) =>
                        setActionForm((p) => ({ ...p, rateProgress: clampProgress(e.target.value) }))
                      }
                    />
                  </label>
                  <label className="appraisal-field" style={{ flex: 1, minWidth: 180 }}>
                    Your comment
                    <textarea
                      rows={2}
                      maxLength={APPRAISAL_LIMITS.comment}
                      value={actionForm.goalComment}
                      onChange={(e) =>
                        setActionForm((p) => ({
                          ...p,
                          goalComment: clampLen(e.target.value, APPRAISAL_LIMITS.comment),
                        }))
                      }
                    />
                    <AppraisalCharCount value={actionForm.goalComment} max={APPRAISAL_LIMITS.comment} />
                  </label>
                  <button type="button" className="appraisal-btn primary" onClick={submitEmployeeRating}>
                    Rate as employee
                  </button>
                </div>
              </div>
            ) : null}

            {actor.canManage && status === GOAL_STATUS.Approved ? (
              <p className="appraisal-framework-hint">
                Waiting for the employee to rate progress on this goal.
              </p>
            ) : null}

            {actor.isOwner && status === GOAL_STATUS.RatedByUser ? (
              <p className="appraisal-framework-hint">
                Waiting for your manager to agree or discuss.
              </p>
            ) : null}

            {actor.canManage && status === GOAL_STATUS.RatedByUser ? (
              <div style={{ marginTop: "0.75rem" }}>
                <p className="appraisal-framework-hint" style={{ marginTop: 0 }}>
                  Manager rating uses the same 1–{goalMax} company scale.
                </p>
                {renderRatingButtons("goalRating", actionForm.goalRating)}
                <div className="appraisal-actions" style={{ flexWrap: "wrap", gap: "0.5rem", marginTop: "0.75rem" }}>
                  <label className="appraisal-field" style={{ flex: 1, minWidth: 180 }}>
                    Manager comment
                    <textarea
                      rows={2}
                      maxLength={APPRAISAL_LIMITS.comment}
                      value={actionForm.managerComment}
                      onChange={(e) =>
                        setActionForm((p) => ({
                          ...p,
                          managerComment: clampLen(e.target.value, APPRAISAL_LIMITS.comment),
                        }))
                      }
                    />
                    <AppraisalCharCount value={actionForm.managerComment} max={APPRAISAL_LIMITS.comment} />
                  </label>
                  <label className="appraisal-field" style={{ flex: 1, minWidth: 180 }}>
                    Discussion notes
                    <textarea
                      rows={2}
                      maxLength={APPRAISAL_LIMITS.comment}
                      value={actionForm.discussionComments}
                      onChange={(e) =>
                        setActionForm((p) => ({
                          ...p,
                          discussionComments: clampLen(e.target.value, APPRAISAL_LIMITS.comment),
                        }))
                      }
                    />
                    <AppraisalCharCount
                      value={actionForm.discussionComments}
                      max={APPRAISAL_LIMITS.comment}
                    />
                  </label>
                  <button type="button" className="appraisal-btn primary" onClick={submitManagerAgree}>
                    Agree
                  </button>
                  <button
                    type="button"
                    className="appraisal-btn"
                    onClick={() =>
                      runAction(discussGoalAsManager, {
                        discussionComments: actionForm.discussionComments,
                        managerComment: actionForm.managerComment,
                      })
                    }
                  >
                    Discuss
                  </button>
                </div>
              </div>
            ) : null}

            {(actor.isOwner || actor.canManage) && status === GOAL_STATUS.ManagerAgreed ? (
              <div style={{ width: "100%", marginTop: "0.75rem" }}>
                <p className="appraisal-framework-hint" style={{ margin: "0 0 0.5rem" }}>
                  Manager agreed. Either you or the manager can mark this goal complete.
                </p>
                <button type="button" className="appraisal-btn primary" onClick={() => runAction(completeGoal)}>
                  Mark complete
                </button>
              </div>
            ) : null}

            {!actor.isOwner && !actor.canManage ? (
              <p className="appraisal-framework-hint">You can view this goal but cannot change its workflow.</p>
            ) : null}
            </div>
          </section>

          {(goal.goalComment || goal.managerComment || goal.discussionComments) && (
            <section className="appraisal-card appraisal-comments-panel">
              <div className="appraisal-comments-panel__head">
                <span className="appraisal-comments-panel__icon" aria-hidden>
                  <i className="bi bi-chat-left-text" />
                </span>
                <div>
                  <h4>Comments</h4>
                  <p>Employee, manager, and discussion notes for this goal.</p>
                </div>
              </div>
              <div className="appraisal-grid-2">
                {goal.goalComment ? (
                  <div>
                    <strong>Employee comment</strong>
                    <p className="appraisal-muted" style={{ padding: 0 }}>
                      {goal.goalComment}
                    </p>
                  </div>
                ) : null}
                {goal.managerComment ? (
                  <div>
                    <strong>Manager comment</strong>
                    <p className="appraisal-muted" style={{ padding: 0 }}>
                      {goal.managerComment}
                    </p>
                  </div>
                ) : null}
                {goal.discussionComments ? (
                  <div>
                    <strong>Discussion notes</strong>
                    <p className="appraisal-muted" style={{ padding: 0 }}>
                      {goal.discussionComments}
                    </p>
                  </div>
                ) : null}
              </div>
            </section>
          )}

          {goal.kpis?.length > 0 && (
            <div className="appraisal-card">
              <h4>KPIs</h4>
              <ul>
                {goal.kpis.map((k) => (
                  <li key={k.id || k.Id}>
                    {k.kpiHeadline || k.KpiHeadline}
                    {(k.amount ?? k.Amount)
                      ? ` — ${k.amount ?? k.Amount}${k.amountType || k.AmountType || ""}`
                      : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      <AppraisalHelpModal helpKey={helpKey} onClose={() => setHelpKey(null)} />
    </div>
  );
};

export default AppraisalGoalsPage;
